import { SignatureV4 } from '@aws-sdk/signature-v4';
import { HttpRequest } from '@aws-sdk/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';
import { getSignedRequest } from './awsCredentials';

const REGION = process.env.REACT_APP_AWS_REGION;
const IDENTITY_POOL_ID = process.env.REACT_APP_IDENTITY_POOL_ID;
const USER_POOL_ID = process.env.REACT_APP_USER_POOL_ID;

/**
 * SigV4-sign an absolute `execute-api` URL and return everything `fetch` needs.
 *
 * Cognito Identity Pool credentials are derived from the OIDC `idToken` (cached
 * in `awsCredentials`), then used to sign the request for the `execute-api`
 * service — the same scheme `baseApi` uses for RTK Query. Extracted here so
 * non-RTK callers (e.g. the CloudFront session bootstrap) can sign requests
 * without duplicating the signing logic.
 *
 * @param {object} params
 * @param {string} params.url    Absolute https URL to sign.
 * @param {string} params.idToken OIDC id token identifying the user.
 * @param {string} [params.method='GET']
 * @param {object} [params.body] JSON-serializable request body.
 * @param {object} [params.headers] Extra headers to include in the signature.
 * @returns {Promise<{ url: string, method: string, headers: object, body?: string }>}
 */
export async function signAwsRequest({ url, idToken, method = 'GET', body, headers = {} }) {
  if (!idToken) throw new Error('Missing idToken');

  const creds = await getSignedRequest(idToken, REGION, IDENTITY_POOL_ID, USER_POOL_ID);

  const signer = new SignatureV4({
    credentials: creds,
    service: 'execute-api',
    region: REGION,
    sha256: Sha256,
  });

  const urlObj = new URL(url);
  const query = Object.fromEntries(urlObj.searchParams.entries());

  const request = new HttpRequest({
    method,
    protocol: urlObj.protocol,
    hostname: urlObj.hostname,
    path: urlObj.pathname,
    query: Object.keys(query).length > 0 ? query : undefined,
    headers: {
      host: urlObj.hostname,
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const signed = await signer.sign(request);

  const fullUrl =
    `${signed.protocol}//${signed.hostname}${signed.path}` +
    (signed.query
      ? '?' +
        Object.entries(signed.query)
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join('&')
      : '');

  return { url: fullUrl, method: signed.method, headers: signed.headers, body: signed.body };
}
